import type { HTMLAttributes } from "react";
import React, { useMemo } from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import type { Account } from "../../../../../shared/domains/account/account";
import { useMonthlyFlux } from "../../../../../shared/domains/accounting-transaction/transaction/use-monthly-flux";
import { Path } from "../../../../core/routing/path";
import { AmountText } from "../../../common/amount-text";
import { DefaultLink } from "../../../common/buttons/default-button";
import { MainColorSpinner } from "../../../common/spinner";
import { StaticAssets } from "../../../styles/custom-assets";
import { theme } from "../../../styles/theme";
import { UIConstants } from "../../../styles/uiConstants";
import { ACCOUNT_QUERY_PARAM } from "../utils/use-selected-account";

interface MonthlyFluxSectionProps extends HTMLAttributes<HTMLDivElement> {
  account: Account;
}

export function MonthlyFluxSection(props: Readonly<MonthlyFluxSectionProps>) {
  const { account, ...rest } = props;
  const { formatMessage, formatDate } = useIntl();
  const { income, outcome, loading } = useMonthlyFlux(account);

  // react-intl carries the app's locale data, moment's locale files are never imported.
  const monthLabel = useMemo(() => formatDate(new Date(), { month: "long" }), [formatDate]);

  return (
    <Section {...rest}>
      <Header>
        <Title>{formatMessage("homeScreen.monthlyFluxTitle", { month: monthLabel })}</Title>
        <SeeAllLink to={`${Path.AllTransactions}?${ACCOUNT_QUERY_PARAM}=${account.id}`}>
          {formatMessage("homeScreen.allTransactionsLink")}
        </SeeAllLink>
      </Header>
      {loading ? (
        <LoaderRow>
          <MainColorSpinner size="24px" />
        </LoaderRow>
      ) : (
        <Tiles>
          <FluxTile>
            <Illustration src={StaticAssets.fluxIncome} alt="" />
            <TileLabel>{formatMessage("homeScreen.monthlyFluxIncome")}</TileLabel>
            <TileAmount $positive amount={income} forceSign />
          </FluxTile>
          <FluxTile>
            <Illustration src={StaticAssets.fluxExpense} alt="" />
            <TileLabel>{formatMessage("homeScreen.monthlyFluxExpense")}</TileLabel>
            <TileAmount amount={outcome} forceSign />
          </FluxTile>
        </Tiles>
      )}
    </Section>
  );
}

const Section = styled.section`
  display: flex;
  flex-direction: column;
  align-self: stretch;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  ${theme.mediumText};
  margin: 0;
  font-size: 14px;
  line-height: 18px;
  color: ${theme.colors.gray[800]};
`;

const SeeAllLink = styled(DefaultLink)`
  ${theme.mediumText};
  font-size: 13px;
  color: ${theme.mainColor()};
`;

const Tiles = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;

  @media (max-width: ${UIConstants.TABLET_BREAKPOINT - 1}px) {
    flex-direction: column;
  }
`;

const FluxTile = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 12px;
  background-color: #ffffff;
`;

const Illustration = styled.img`
  width: 48px;
  height: 48px;
`;

const LoaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
`;

const TileLabel = styled.span`
  ${theme.mediumText};
  flex: 1;
  font-size: 14px;
  line-height: 18px;
  color: #000000;
`;

/** Green marks what came in; what went out stays neutral, its minus sign carries the meaning. */
const TileAmount = styled(AmountText)<{ $positive?: boolean }>`
  ${theme.boldText};
  font-size: 17px;
  color: ${(props) => (props.$positive ? "#26cc8f" : "#000000")};
`;
